import "react-toastify/dist/ReactToastify.min.css";
import "@/utils/i18n";

import * as queryString from "query-string";

import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import React, { Suspense, useCallback, useEffect, useMemo } from "react";
import {
  appStage,
  isImpersonatingCookieName,
  isLoggedInCookieName,
  nodeEnv,
} from "@/environment";
import { createPublicPerson, linkPublicPerson } from "@/slices/persons";
import {
  getAllAnonymousIds,
  getAnonymousId,
  replaceAnonymousId,
} from "@/utils/analytics";
import { useDispatch, useSelector } from "react-redux";

import AnalyticsBlock from "@/public/features/dev/AnalyticsBlock/AnalyticsBlock";
import Cookies from "js-cookie";
import Error404 from "@/public/core/components/Error404";
import ErrorTest from "@/public/features/dev/ErrorTest/ErrorTest";
import GlobalStyle from "@/styles/globalStyle";
import Layout from "@/dashboard/shared/components/Layout/Layout";
import { PrivateRoute } from "@/dashboard/shared/components/router/PrivateRoute";
import { PuckToastContainer } from "@/public/core/components/Toast";
import SettingsLayout from "@/dashboard/features/settings/shared/components/SettingsLayout";
import ShowEnv from "@/public/features/dev/ShowEnv/ShowEnv";
import Unauthorized from "@/dashboard/features/auth/Unauthorized/Unauthorized";
import { cookieDomain } from "@/environment";
import { getInjectedContentPagePermalink } from "@/utils/getInjectedContentPagePermalink";
import importRetry from "@/utils/importRetry";
import { isLoggedIn } from "@/utils/api";
import { keepSessionAlive } from "@/slices/employers";
import useInterval from "@/dashboard/core/hooks/useInterval";

/* PUCK ADMIN PAGES */
const Impersonate = React.lazy(() =>
  importRetry(() => import("@/dashboard/features/auth/Impersonate/Impersonate"))
);

/* AUTH PAGES */
const ClaimAccount = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/auth/ClaimAccount/ClaimAccount")
  )
);
const ClaimAccountSignup = React.lazy(() =>
  import("@/dashboard/features/auth/ClaimAccountSignup/ClaimAccountSignup")
);
const ClaimAccountProfile = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/auth/ClaimAccountProfile/ClaimAccountProfile")
  )
);
const ForgotPassword = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/auth/ForgotPassword/ForgotPassword")
  )
);
const ResetPassword = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/auth/ResetPassword/ResetPassword")
  )
);
const Login = React.lazy(() =>
  importRetry(() => import("@/dashboard/features/auth/Login/Login"))
);
const Logout = React.lazy(() =>
  importRetry(() => import("@/dashboard/features/auth/Logout/Logout"))
);

/* DASHBOARD PAGES */
const Root = React.lazy(() =>
  importRetry(() => import("@/dashboard/shared/routes/Root/Root"))
);
const Home = React.lazy(() =>
  importRetry(() => import("@/dashboard/features/homepage/Home/Home"))
);
const ShareLinkDetail = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/shared/routes/ShareLinkDetail/ShareLinkDetail")
  )
);
const DashboardPodcastEdit = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/content_library/PodcastEdit/PodcastEdit")
  )
);
const DashboardPodcastDetail = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/content_library/PodcastDetail/PodcastDetail")
  )
);
const DashboardJobPageEdit = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/job_pages/JobPageEdit/JobPageEdit")
  )
);
const DashboardJobPageDetail = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/job_pages/JobPageDetail/JobPageDetail")
  )
);
const DashboardJobPages = React.lazy(() =>
  importRetry(() => import("@/dashboard/features/job_pages/JobPages/JobPages"))
);
const DashboardContentLibrary = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/content_library/ContentLibrary/ContentLibrary")
  )
);
const DashboardSettingsProfile = React.lazy(() =>
  importRetry(() => import("@/dashboard/features/settings/Profile/Profile"))
);
const DashboardSettingsAts = React.lazy(() =>
  importRetry(() => import("@/dashboard/features/settings/Ats/Ats"))
);
const DashboardSettingsDepartments = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/settings/Departments/Departments")
  )
);
const DashboardSettingsOffices = React.lazy(() =>
  importRetry(() => import("@/dashboard/features/settings/Offices/Offices"))
);
const DashboardSettingsMembers = React.lazy(() =>
  importRetry(() => import("@/dashboard/features/settings/Members/Members"))
);
const DashboardSettingsSmartLinks = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/settings/SmartLinks/SmartLinks")
  )
);
const DashboardSettingsEmail = React.lazy(() =>
  importRetry(() => import("@/dashboard/features/settings/Email/Email"))
);
const DashboardTeaserEdit = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/content_library/TeaserEdit/TeaserEdit")
  )
);
const DashboardTeasers = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/content_library/Teasers/Teasers")
  )
);
const LeverCallback = React.lazy(() =>
  importRetry(() => import("@/dashboard/features/integrations/LeverCallback"))
);
const OutlookCallback = React.lazy(() =>
  importRetry(() => import("@/dashboard/features/integrations/OutlookCallback"))
);
const ContentPageEdit = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/content_pages/ContentPageEdit/ContentPageEdit")
  )
);
const InterviewGuideDetail = React.lazy(() =>
  importRetry(() =>
    import(
      "@/dashboard/features/content_pages/InterviewGuideDetail/InterviewGuideDetail"
    )
  )
);
const InterviewGuides = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/content_pages/InterviewGuides/InterviewGuides")
  )
);
const CampaignPageDetail = React.lazy(() =>
  importRetry(() =>
    import(
      "@/dashboard/features/content_pages/CampaignPageDetail/CampaignPageDetail"
    )
  )
);
const CampaignPages = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/content_pages/CampaignPages/CampaignPages")
  )
);
const DashboardTalentSearch = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/talent_search/TalentSearch/TalentSearch")
  )
);
const DashboardTalentLists = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/talent_lists/TalentLists/TalentLists")
  )
);
const DashboardTalentListDetail = React.lazy(() =>
  importRetry(() =>
    import(
      "@/dashboard/features/talent_lists/TalentListDetail/TalentListDetail"
    )
  )
);
const DashboardSequenceDetail = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/sequences/SequenceDetail/SequenceDetail")
  )
);
const DashboardSequences = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/sequences/Sequences/Sequences")
  )
);
const DashboardSequenceEdit = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/sequences/SequenceEdit/SequenceEdit")
  )
);
const DashboardSequenceReview = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/sequences/SequenceReview/SequenceReview")
  )
);
const DashboardSequenceNotificationEdit = React.lazy(() =>
  importRetry(() =>
    import(
      "@/dashboard/features/sequences/SequenceNotificationEdit/SequenceNotificationEdit"
    )
  )
);
const DashboardSequenceNotificationDetail = React.lazy(() =>
  importRetry(() =>
    import(
      "@/dashboard/features/sequences/SequenceNotificationDetail/SequenceNotificationDetail"
    )
  )
);
const DashboardSequenceNotifications = React.lazy(() =>
  importRetry(() =>
    import(
      "@/dashboard/features/sequences/SequenceNotifications/SequenceNotifications"
    )
  )
);

/* PUBLIC CANDIDATE PAGES */
const JobBoardPublic = React.lazy(() =>
  importRetry(() =>
    import("@/public/features/job_boards/JobBoardPublic/JobBoardPublic")
  )
);
const JobPagePublic = React.lazy(() =>
  importRetry(() =>
    import("@/public/features/job_pages/JobPagePublic/JobPagePublic")
  )
);
const JobPageApplicationPublic = React.lazy(() =>
  importRetry(() =>
    import(
      "@/public/features/job_pages/JobPageApplicationPublic/JobPageApplicationPublic"
    )
  )
);
const JobClipPublic = React.lazy(() =>
  importRetry(() =>
    import("@/public/features/job_clips/JobClipPublic/JobClipPublic")
  )
);
const AppReminderOptOut = React.lazy(() =>
  importRetry(() =>
    import("@/public/shared/routes/AppReminderOptOut/AppReminderOptOut")
  )
);
const ContentPagePublic = React.lazy(() =>
  importRetry(() =>
    import(
      "@/public/features/content_pages/ContentPagePublic/ContentPagePublic"
    )
  )
);
const ContentPagePublicSales = React.lazy(() =>
  importRetry(() =>
    import(
      "@/public/features/content_pages/ContentPagePublicSales/ContentPagePublicSales"
    )
  )
);
const DashboardApplicantMatch = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/applicant_match/ApplicantMatch/ApplicantMatch")
  )
);
const DashboardApplicantMatchDetail = React.lazy(() =>
  importRetry(() =>
    import(
      "@/dashboard/features/applicant_match/ApplicantMatchDetail/ApplicantMatchDetail"
    )
  )
);
const DashboardPipelines = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/pipelines/Pipelines/Pipelines")
  )
);
const DashboardPipelineDetail = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/pipelines/PipelineDetail/PipelineDetail")
  )
);
/* EMBED PAGE */
const Embed = React.lazy(() =>
  importRetry(() => import("@/public/shared/routes/Embed/Embed"))
);
/* PDF REPORT DOWNLOAD PAGE */
const PdfReportDownload = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/shared/routes/PdfReportDownload/PdfReportDownload")
  )
);
/* PUBLIC DAILY SPOTLIGHT PAGES */
const DailySpotlightPublic = React.lazy(() =>
  importRetry(() =>
    import(
      "@/public/features/daily_spotlights/DailySpotlightPublic/DailySpotlightPublic"
    )
  )
);

/* SELF-SERVE PAGES */
const SelfServeSignup = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/self_serve/SelfServeSignup/SelfServeSignup")
  )
);
const SelfServeProfile = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/self_serve/SelfServeProfile/SelfServeProfile")
  )
);
const SelfServeAtsAuth = React.lazy(() =>
  importRetry(() =>
    import("@/dashboard/features/self_serve/SelfServeAtsAuth/SelfServeAtsAuth")
  )
);

// Component that handles pathname changes (must be inside Router)
const AppContent = () => {
  const location = useLocation();

  // Compute injected content page permalink synchronously during render
  // This prevents 404 flash on both initial load and subsequent navigation
  const injectedContentPagePermalink = useMemo(() => {
    return getInjectedContentPagePermalink(location.pathname);
  }, [location.pathname]);

  const dispatch = useDispatch();
  const user = useSelector((state) => state?.auth?.user || null);

  // Send a keep-alive ping to the server
  const keepAlive = useCallback(() => {
    if (isLoggedIn() && user?.employerId) {
      dispatch(keepSessionAlive(user.employerId));

      // Extend the login-related cookies too
      Cookies.set(isLoggedInCookieName, "true", {
        domain: cookieDomain,
        expires: 365,
      });
      if (Cookies.get(isImpersonatingCookieName) === "true") {
        Cookies.set(isImpersonatingCookieName, "true", {
          domain: cookieDomain,
          expires: 365,
        });
      }
    }
  }, [dispatch, user?.employerId]);

  // Use the useInterval hook for keep alive
  const { start: startKeepAlive } = useInterval(
    keepAlive,
    25 * 60 * 1000,
    false
  ); // 25m timer, don't run immediately

  // Start the keep alive interval
  useEffect(() => {
    startKeepAlive();
  }, [startKeepAlive]);

  const _linkPublicPerson = useCallback(async () => {
    // See if have puckAnonymousId in query params
    const queryParams = queryString.parse(document.location.search);

    if (queryParams.puckAnonymousId) {
      const anonymousId = queryParams.puckAnonymousId;

      const payload = {
        anonymousIds: getAllAnonymousIds(anonymousId),
      };

      const resultAction = await dispatch(linkPublicPerson({ payload }));
      if (linkPublicPerson.fulfilled.match(resultAction)) {
        // Replace the Puck anonymousId cookie with this new ID
        replaceAnonymousId(anonymousId);
      }
    }
  }, [dispatch]);

  const _createShareLinkPerson = useCallback(async () => {
    // See if have any share link params
    const queryParams = queryString.parse(document.location.search);

    if (
      (queryParams["p-fn"] ||
        queryParams["p-ln"] ||
        queryParams["p-un"] ||
        queryParams["p-e"]) &&
      queryParams["p-s"]
    ) {
      const payload = {
        anonymousIds: getAllAnonymousIds(),
        shareLinkPermalink: queryParams["p-s"],
        person: {
          source: "share-link",
        },
      };
      if (queryParams["p-fn"]) {
        payload.person.firstName = queryParams["p-fn"];
      }
      if (queryParams["p-ln"]) {
        payload.person.lastName = queryParams["p-ln"];
      }
      if (queryParams["p-un"]) {
        payload.person.fullName = queryParams["p-un"];
      }
      if (queryParams["p-e"]) {
        payload.person.email = queryParams["p-e"];
      }

      if (payload.anonymousIds) {
        dispatch(createPublicPerson({ payload }));
      }
    }
  }, [dispatch]);

  // Initialize on mount
  useEffect(() => {
    // Make sure we have a Puck anonymousId set
    getAnonymousId();

    // Link anonymous
    _linkPublicPerson();

    // Create short link person
    _createShareLinkPerson();
  }, [_linkPublicPerson, _createShareLinkPerson]);

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route
          exact
          path="/auth/lever/callback"
          element={
            <PrivateRoute>
              <LeverCallback />
            </PrivateRoute>
          }
        />
        <Route
          path="/auth/outlook/callback"
          element={
            <PrivateRoute>
              <OutlookCallback />
            </PrivateRoute>
          }
        />
        <Route path="/login" element={<Navigate to="/employers/login" />} />
        <Route path="/employers/login" element={<Login />} />
        <Route path="/employers/claim-account" element={<ClaimAccount />} />
        <Route
          path="/employers/claim-account-signup"
          element={<ClaimAccountSignup />}
        />
        <Route
          path="/employers/claim-account-profile"
          element={<ClaimAccountProfile />}
        />
        <Route path="/employers/forgot-password" element={<ForgotPassword />} />
        <Route path="/employers/reset-password" element={<ResetPassword />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/analytics/block" element={<AnalyticsBlock />} />
        <Route
          path="/impersonate"
          element={
            <PrivateRoute>
              <Impersonate />
            </PrivateRoute>
          }
        />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Root />
            </PrivateRoute>
          }
        />
        <Route
          path="/employers"
          element={
            <PrivateRoute>
              <Root />
            </PrivateRoute>
          }
        />
        {/* Dashboard Layout with nested routes */}
        <Route
          path="/employers/*"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route path="home" element={<Home />} />
          <Route path="talent-search" element={<DashboardTalentSearch />} />
          <Route
            path="talent-search/person/:personPermalink"
            element={<DashboardTalentSearch />}
          />
          <Route
            path="job-page/:jobPagePermalink"
            element={<DashboardJobPageDetail />}
          />
          <Route
            path="pipeline/:jobPagePermalink"
            element={<DashboardPipelineDetail />}
          />
          <Route
            path="pipeline/:jobPagePermalink/application/:applicationPermalink"
            element={<DashboardPipelineDetail />}
          />
          <Route
            path="applicant-match/:jobPagePermalink"
            element={<DashboardApplicantMatchDetail />}
          />
          <Route
            path="applicant-match/:jobPagePermalink/application/:applicationPermalink"
            element={<DashboardApplicantMatchDetail />}
          />
          <Route
            path="content-library/podcast/:jobClipPermalink"
            element={<DashboardPodcastDetail />}
          />
          <Route
            path="content-library/podcast/:jobClipPermalink/edit"
            element={<DashboardPodcastEdit />}
          />
          <Route
            path="talent-list/:personListPermalink"
            element={<DashboardTalentListDetail />}
          />
          <Route
            path="sequence-campaign/:sequencePermalink"
            element={<DashboardSequenceDetail />}
          />
          <Route
            path="sequence-notification/:sequencePermalink"
            element={<DashboardSequenceNotificationDetail />}
          />
          <Route
            path="interview-guide/:interviewGuidePermalink"
            element={<InterviewGuideDetail />}
          />
          <Route
            path="campaign-page/:campaignPagePermalink"
            element={<CampaignPageDetail />}
          />
          <Route path="pipelines" element={<DashboardPipelines />} />
          <Route path="content-library" element={<DashboardContentLibrary />} />
          <Route path="job-pages" element={<DashboardJobPages />} />
          <Route path="talent-lists" element={<DashboardTalentLists />} />
          <Route path="sequence-campaigns" element={<DashboardSequences />} />
          <Route
            path="sequence-campaign/:sequencePermalink/edit"
            element={<DashboardSequenceEdit />}
          />
          <Route
            path="sequence-campaign/:sequencePermalink/review"
            element={<DashboardSequenceReview />}
          />
          <Route
            path="sequence-notifications"
            element={<DashboardSequenceNotifications />}
          />
          <Route
            path="sequence-notification/:sequencePermalink/edit"
            element={<DashboardSequenceNotificationEdit />}
          />
          <Route path="applicant-match" element={<DashboardApplicantMatch />} />
          <Route path="interview-guides" element={<InterviewGuides />} />
          <Route path="campaign-pages" element={<CampaignPages />} />
          <Route path="teasers" element={<DashboardTeasers />} />
          <Route path="settings/*" element={<SettingsLayout />}>
            <Route path="profile" element={<DashboardSettingsProfile />} />
            <Route path="ats" element={<DashboardSettingsAts />} />
            <Route path="email" element={<DashboardSettingsEmail />} />
            <Route path="members" element={<DashboardSettingsMembers />} />
            <Route
              path="departments"
              element={<DashboardSettingsDepartments />}
            />
            <Route
              path="smart-links"
              element={<DashboardSettingsSmartLinks />}
            />
            <Route path="offices" element={<DashboardSettingsOffices />} />
          </Route>

          {/* Share Link Detail Routes */}
          <Route
            path="interview-guide/:interviewGuidePermalink/share-link/:shareLinkPermalink"
            element={<ShareLinkDetail />}
          />
          <Route
            path="campaign-page/:campaignPagePermalink/share-link/:shareLinkPermalink"
            element={<ShareLinkDetail />}
          />
          <Route
            path="job-page/:jobPagePermalink/share-link/:shareLinkPermalink"
            element={<ShareLinkDetail />}
          />
          <Route
            path="content-library/podcast/:jobClipPermalink/share-link/:shareLinkPermalink"
            element={<ShareLinkDetail />}
          />
        </Route>

        {/* Embed */}
        <Route
          path="/job-clip/:jobClipPermalink/track/:jobClipTrackPermalink/embed"
          element={<Embed />}
        />
        <Route path="/job-clip/:jobClipPermalink/embed" element={<Embed />} />

        {/* Job Clips */}
        <Route path="/job-clip/:jobClipPermalink" element={<JobClipPublic />} />

        {/* Job Boards */}
        <Route
          path="/job-board/:jobBoardPermalink/job/:jobPagePermalink/apply"
          element={<JobPageApplicationPublic />}
        />
        <Route
          path="/job-board/:jobBoardPermalink/job/:jobPagePermalink"
          element={<JobPagePublic />}
        />
        <Route
          path="/job-board/:jobBoardPermalink"
          element={<JobBoardPublic />}
        />

        {/* Content Pages */}
        <Route
          path="/page/:contentPagePermalink"
          element={<ContentPagePublic />}
        />

        {/* Sales */}
        <Route
          path="/sales/:employerPermalink/page/:contentPagePermalink"
          element={<ContentPagePublicSales />}
        />

        {/* Employer Podcasts - moved to nested routes */}

        {/* Employer Job Pages */}
        <Route
          path="/employers/job-page/new"
          element={
            <PrivateRoute>
              <DashboardJobPageEdit />
            </PrivateRoute>
          }
        />
        <Route
          path="/employers/job-page/:jobPagePermalink/edit"
          element={
            <PrivateRoute>
              <DashboardJobPageEdit />
            </PrivateRoute>
          }
        />
        {/* Job page routes moved to nested routes */}

        {/* Employer Video Clips */}
        <Route
          path="/employers/video-clip/edit"
          element={
            <PrivateRoute>
              <DashboardTeaserEdit />
            </PrivateRoute>
          }
        />
        <Route
          path="/employers/video-clip"
          element={
            <PrivateRoute>
              <DashboardTeasers />
            </PrivateRoute>
          }
        />

        {/* Employer Talent Search - moved to nested routes */}
        {/* Redirect old talent pool to talent search */}
        <Route
          path="/employers/talent-pool"
          element={<Navigate to="/employers/talent-search" />}
        />

        {/* Employer Talent Lists - moved to nested routes */}

        {/* Employer Sequence Campaigns - moved to nested routes */}

        {/* Employer Sequence Notifications - moved to nested routes */}

        {/* Employer Jobs - moved to nested routes */}
        {/* Employer Pipelines - moved to nested routes */}

        {/* Employer Content Pages */}
        <Route
          path="/employers/page/:contentPagePermalink/edit"
          element={
            <PrivateRoute>
              <ContentPageEdit />
            </PrivateRoute>
          }
        />
        {/* Interview guide routes moved to nested routes */}
        {/* Content page job page routes moved to nested routes */}
        {/* Campaign page routes moved to nested routes */}

        <Route
          path="/employers/settings"
          element={
            <PrivateRoute>
              <Navigate to="/employers/settings/ats" />
            </PrivateRoute>
          }
        />

        {/* Employer PDF Reports */}
        <Route
          path="/employers/:employerPermalink/pdf-report/:pdfReportPermalink/download"
          element={
            <PrivateRoute>
              <PdfReportDownload />
            </PrivateRoute>
          }
        />

        {/* App Reminders */}
        <Route
          path="/app-reminder/opt-out/:sequenceTargetPermalink"
          element={<AppReminderOptOut />}
        />

        {/* Daily Spotlights */}
        <Route
          path="/daily-highlights/:date"
          element={<DailySpotlightPublic />}
        />
        <Route path="/daily-highlights" element={<DailySpotlightPublic />} />

        {/* Self-Serve */}
        <Route
          path="/employers/self-serve/signup"
          element={<SelfServeSignup />}
        />
        <Route
          path="/employers/self-serve/profile"
          element={
            <PrivateRoute>
              <SelfServeProfile />
            </PrivateRoute>
          }
        />
        <Route
          path="/employers/self-serve/ats"
          element={
            <PrivateRoute>
              <SelfServeAtsAuth />
            </PrivateRoute>
          }
        />

        {(appStage === "uat" || nodeEnv === "development") && (
          <>
            <Route path="/__env" element={<ShowEnv />} />
            <Route path="/__error" element={<ErrorTest />} />
            <Route path="/__unauthorized" element={<Unauthorized />} />
          </>
        )}

        {/* dynamically add the content page route */}
        {injectedContentPagePermalink && (
          <Route
            path="*" // or use `useLocation().pathname` if you want to be precise
            element={
              <ContentPagePublic
                injectedContentPagePermalink={injectedContentPagePermalink}
              />
            }
          />
        )}

        <Route path="/404" element={<Error404 />} />
        <Route path="*" element={<Error404 />} />
      </Routes>
    </Suspense>
  );
};

const App = () => {
  return (
    <>
      <GlobalStyle />
      <PuckToastContainer />
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </>
  );
};

export default App;
